import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { Menu, X, Search as SearchIcon, ArrowUpRight } from "lucide-react";
import { NAV, BRAND, BLOG_POSTS, AI_TOOLS, AFFILIATE_PRODUCTS, EBOOKS } from "@/data/content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogTrigger,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

const SEARCH_INDEX = [
    ...BLOG_POSTS.map((p) => ({ type: "Blog", title: p.title, to: `/blog/${p.slug}`, blurb: p.excerpt })),
    ...AI_TOOLS.map((t) => ({ type: "AI Tool", title: t.name, to: `/ai-tools#${t.slug}`, blurb: t.tagline })),
    ...AFFILIATE_PRODUCTS.map((p) => ({ type: "Affiliate", title: p.name, to: `/affiliate#${p.slug}`, blurb: p.blurb })),
    ...EBOOKS.map((e) => ({ type: "eBook", title: e.title, to: `/ebooks#${e.slug}`, blurb: e.subtitle })),
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [q, setQ] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const results = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return [];
        return SEARCH_INDEX.filter(
            (r) =>
                r.title.toLowerCase().includes(s) ||
                (r.blurb && r.blurb.toLowerCase().includes(s)),
        ).slice(0, 8);
    }, [q]);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-stone-50/85 backdrop-blur-xl border-b border-stone-200"
                    : "bg-transparent"
            }`}
        >
            <div className="container-editorial flex items-center justify-between h-16 md:h-20">
                <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
                    <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground grid place-items-center font-serif text-lg italic group-hover:rotate-6 transition-transform">
                        A
                    </span>
                    <span className="font-serif text-xl tracking-tight">{BRAND.name}</span>
                </Link>

                <nav className="hidden lg:flex items-center gap-7">
                    {NAV.map((item) =>
                        item.external ? (
                            <a
                                key={item.to}
                                href={item.to}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="text-sm link-underline text-accent font-semibold hover:text-primary"
                            >
                                {item.label}
                            </a>
                        ) : (
                            <Link
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`text-sm link-underline ${
                                    location.pathname === item.to
                                        ? "text-primary font-semibold"
                                        : "text-stone-700 hover:text-primary"
                                }`}
                            >
                                {item.label}
                            </Link>
                        ),
                    )}
                </nav>

                <div className="flex items-center gap-2">
                    <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
                        <DialogTrigger asChild>
                            <button
                                data-testid="header-search-btn"
                                className="p-2 rounded-full hover:bg-stone-200/60 transition"
                                aria-label="Search"
                            >
                                <SearchIcon className="h-5 w-5 text-stone-700" />
                            </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-xl">
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl">Search the hub</DialogTitle>
                                <DialogDescription>Guides, tools, products & ebooks.</DialogDescription>
                            </DialogHeader>
                            <Input
                                autoFocus
                                data-testid="global-search-input"
                                placeholder="Try: chatgpt, kuronazo, kdp, warriorplus…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                            />
                            <div className="max-h-80 overflow-y-auto divide-y divide-stone-200">
                                {results.length === 0 && q && (
                                    <p className="text-sm text-stone-500 py-6 text-center">
                                        No results for “{q}”.
                                    </p>
                                )}
                                {results.map((r, i) => (
                                    <Link
                                        key={i}
                                        to={r.to}
                                        data-testid={`search-result-${i}`}
                                        onClick={() => setSearchOpen(false)}
                                        className="flex items-start gap-3 py-3 group"
                                    >
                                        <span className="text-[10px] tracking-widest uppercase font-bold text-accent mt-1">
                                            {r.type}
                                        </span>
                                        <div className="flex-1">
                                            <p className="font-medium text-stone-900 group-hover:text-primary">
                                                {r.title}
                                            </p>
                                            {r.blurb && (
                                                <p className="text-xs text-stone-500 line-clamp-1">
                                                    {r.blurb}
                                                </p>
                                            )}
                                        </div>
                                        <ArrowUpRight className="h-4 w-4 text-stone-400 group-hover:text-primary" />
                                    </Link>
                                ))}
                            </div>
                        </DialogContent>
                    </Dialog>

                    <Button
                        asChild
                        data-testid="header-cta-btn"
                        className="hidden md:inline-flex rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-5"
                    >
                        <Link to="/ebooks">Free eBooks</Link>
                    </Button>

                    <button
                        data-testid="mobile-menu-toggle"
                        className="lg:hidden p-2 rounded-full hover:bg-stone-200/60"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-stone-200 bg-stone-50">
                    <nav className="container-editorial py-4 flex flex-col gap-1">
                        {NAV.map((item) =>
                            item.external ? (
                                <a
                                    key={item.to}
                                    href={item.to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="py-2 text-accent font-semibold"
                                >
                                    {item.label} ↗
                                </a>
                            ) : (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="py-2 text-stone-800 hover:text-primary"
                                >
                                    {item.label}
                                </Link>
                            ),
                        )}
                    </nav>
                </div>
            )}
        </header>
    );
}
